import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Upload, Trash2, Loader2, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaManager({ token }) {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/media`);
      setMedia(data);
    } catch {
      setError("Impossibile caricare l'elenco dei file.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFiles = async (files) => {
    setUploading(true);
    setError("");
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/admin/media`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      await load();
    } catch {
      setError("Caricamento non riuscito per uno o più file.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/media/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMedia((m) => m.filter((x) => x.id !== id));
    } catch {
      setError("Eliminazione non riuscita.");
    }
  };

  return (
    <div data-testid="media-manager">
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)}
        data-testid="media-file-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 rounded-full bg-terracotta text-panna font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 transition-all duration-300 hover:bg-[#b25c42] disabled:opacity-60"
        data-testid="media-upload-button"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "Caricamento in corso..." : "Carica foto o video"}
      </button>
      {error && (
        <p className="mt-3 text-sm text-red-700" data-testid="media-error">{error}</p>
      )}

      {media.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {media.map((m) => (
            <div
              key={m.id}
              className="relative group rounded-xl border border-sand overflow-hidden bg-panna aspect-square"
              data-testid={`media-item-${m.id}`}
            >
              {m.kind === "video" ? (
                <video
                  src={`${API}/media/${m.id}/file`}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                />
              ) : (
                <img
                  src={`${API}/media/${m.id}/file`}
                  alt={m.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              {m.kind === "video" && (
                <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-charcoal/60 flex items-center justify-center">
                  <Film className="w-3.5 h-3.5 text-panna" />
                </span>
              )}
              <button
                onClick={() => remove(m.id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-charcoal/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-red-700"
                data-testid={`media-delete-${m.id}`}
                aria-label="Elimina file"
              >
                <Trash2 className="w-3.5 h-3.5 text-panna" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
